/**
 * IntegrationMappingPanel — shared field mapping panel for all integrations.
 *
 * Replaces the near-identical ShopifyPanel, AccelaPanel, and any future
 * integration panel. The only integration-specific values are passed as props:
 *   - fieldKey        the raw API key name ("ShopifyField", "accelaField", etc.)
 *   - fieldLabel      column header shown to admins ("Shopify Field", etc.)
 *   - apiPrefix       URL segment ("Shopify/Integrations", "Accela/Integrations")
 *   - getFieldsPath   endpoint for fetching available fields ("GetShopifyFields")
 *   - scopeOptions    list of scope/record type strings for the selector
 *
 * Internal state always uses the normalized `IntegrationFieldMap.field` key.
 * Serialization to/from the API maps `field` ↔ `fieldKey` transparently.
 */

import useApi from "@/hooks/useApi";
import React, {
  useCallback,
  useEffect,
  useId,
  useState
} from "react";
import { SingleValue } from "react-select";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import CustomSelect from "../CustomSelect";
// Note: AlertTriangle/IconClose icons removed — replaced by ds button/modal styles.
import IntegrationMappingRow, { Option } from "./IntegrationMappingRow";
import { ChevronDown, HelpCircle } from "@/components/ds/icons";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  IconButton,
} from "@/components/ds";

// ---------------------------------------------------------------------------
// Column header help — always-accessible replacement for hover-only `title=`
// tooltips. The icon's accessible name (aria-label) is exposed to assistive
// tech and keyboard focus regardless of hover/touch support; `title` is kept
// as a bonus desktop hover hint, never as the sole explanation.
// ---------------------------------------------------------------------------

function ColumnHelp({ description }: { description: string }) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  return (
    <span className="relative inline-flex align-middle ml-1">
      <button
        type="button"
        tabIndex={0}
        aria-label={description}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        onClick={() => setOpen(true)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex text-zinc-600 hover:text-zinc-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/40 rounded-full"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          className="absolute z-20 top-full left-1/2 -translate-x-1/2 mt-1 w-48 rounded-lg bg-zinc-800 text-white text-[11px] leading-snug px-2.5 py-1.5 shadow-lg normal-case"
        >
          {description}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Normalized mapping row — all integrations use this internally. */
export interface IntegrationFieldMap {
  id: number;
  /** Normalized: the selected integration field as a react-select Option. */
  field: SingleValue<Option>;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  defaultValue: string;
  scopeType: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IntegrationMappingPanelProps {
  /** Panel starts with this scope type selected. */
  scopeType: string;
  /** The config ID for this integration instance. */
  selectedConfigId: number;
  /** Needed to resolve the config list hook internally if used; pass 0 if unused. */
  teamId: number;
  /** Callback to surface "All changes saved" indicator in the parent. */
  showSavedMessage: () => void;
  /** Disable the "Delete Record" button when this is the only panel. */
  isDeleteDisabled: boolean;

  // -- Integration-specific config --

  /** Raw key name sent to/received from the API, e.g. "ShopifyField". */
  fieldKey: string;
  /** Column header, e.g. "Shopify Field". */
  fieldLabel: string;
  /** URL prefix for all API calls, e.g. "Shopify/Integrations". */
  apiPrefix: string;
  /** Path used to fetch available fields, e.g. "GetShopifyFields". */
  getFieldsPath: string;
  /** Scope / record type options shown in the panel header selector. */
  scopeOptions: string[];
  /** Optional live filter — only rows whose field name matches are shown. */
  filterText?: string;
  /** Called when a new row is added — clears any active search filter so the new (blank) row isn't immediately hidden. */
  onClearFilter?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function IntegrationMappingPanel(props: IntegrationMappingPanelProps) {
  const {
    fieldKey,
    fieldLabel,
    apiPrefix,
    getFieldsPath,
    scopeOptions: scopeOptionsList,
    selectedConfigId,
    showSavedMessage,
    isDeleteDisabled,
    filterText = "",
    onClearFilter
  } = props;

  const api = useApi();

  const [mappedFields, setMappedFields] = useState<IntegrationFieldMap[]>([]);
  const [scopeType, setScopeType] = useState<string>(props.scopeType);
  const [metadata, setMetadata] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Scope-level delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [scopeTypeToDelete, setScopeTypeToDelete] = useState<string | null>(null);

  // Field-level delete modal
  const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{ index: number; fieldName: string } | null>(null);

  // Per-row validation errors (duplicate field selection). Keyed by row index.
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
  // Save-request failure — never silently swallowed (see saveRecord below).
  const [saveError, setSaveError] = useState<string | null>(null);

  // ── fetch available fields for the current scope ──────────────────────────
  useEffect(() => {
    async function fetchMetadata() {
      if (!scopeType || selectedConfigId == null) {
        setMetadata([]);
        return;
      }
      setIsLoading(true);
      try {
        const resp = await api.messaging.post(
          `${apiPrefix}/${getFieldsPath}/${selectedConfigId}`,
          scopeType
        );
        const json = await resp.json();
        const newOptions = Object.values(json).map((key) => ({
          value: key,
          label: key as string
        })) as Option[];
        setMetadata(newOptions);
      } catch (err) {
        console.error(`${fieldLabel}: failed to load fields`, err);
        setMetadata([]);
      } finally {
        setIsLoading(false);
      }
    }
    fetchMetadata();
  }, [api.messaging, scopeType, selectedConfigId, apiPrefix, getFieldsPath, fieldLabel]);

  // ── fetch saved mappings for the current scope ────────────────────────────
  const fetchMapping = useCallback(async () => {
    if (selectedConfigId == null || !scopeType) return;
    try {
      const resp = await api.messaging.get(
        `${apiPrefix}/Mapping/${scopeType}/${selectedConfigId}`
      );
      if (!resp.ok) {
        console.error(`${fieldLabel}: failed to load mapping`, resp.statusText);
        return;
      }
      const json = await resp.json();
      // Normalize: API key → internal `field` property
      const normalized: IntegrationFieldMap[] = json.map((el: any) => ({
        ...el,
        field: el[fieldKey] != null
          ? { value: el[fieldKey], label: el[fieldKey] }
          : { value: "", label: "" }
      }));
      setMappedFields(normalized);
      setFieldErrors({});
    } catch (err) {
      console.error(`${fieldLabel}: error loading mapping`, err);
      setMappedFields([]);
    }
  }, [api.messaging, scopeType, selectedConfigId, apiPrefix, fieldKey, fieldLabel]);

  useEffect(() => {
    if (!scopeType) return;
    fetchMapping();
  }, [fetchMapping, scopeType]);

  // ── save all rows ─────────────────────────────────────────────────────────
  // Called explicitly from the handlers that make a real user-initiated change
  // (updateMappedField) — NOT from a useEffect watching `mappedFields`, which
  // previously fired on every state change including the one `fetchMapping`
  // makes right after loading data from the server. That race POSTed the
  // just-fetched (and, pre-fix, wrongly-blanked) values right back to the
  // server on every page load, silently wiping real saved mappings.
  //
  // Only ever surfaces "All changes saved" (via `showSavedMessage`) when a
  // save genuinely reached the server and returned success — a rejected
  // duplicate, an empty/no-op payload, or a failed request all skip it and
  // (for a real failure) set an inline error instead, per this repo's rule
  // against silently swallowing a failed fetch.
  async function saveRecord(fields: IntegrationFieldMap[]) {
    if (selectedConfigId == null) return;
    try {
      // Rows with no field selected are not a valid saved mapping — never
      // persist a blank selection as if it were a real choice.
      const payload = fields
        .filter((el) => !!el.field?.value)
        .map((el) => ({
          [fieldKey]: el.field?.value ?? "",
          id: el.id,
          create: el.create,
          usedForSearching: el.usedForSearching,
          showWhenSelected: el.showWhenSelected,
          showInSearch: el.showInSearch,
          defaultValue: el.defaultValue,
          scopeType: el.scopeType
        }));
      if (payload.length === 0) return;
      const resp = await api.messaging.post(
        `${apiPrefix}/Mapping/${selectedConfigId}`,
        { Mappings: payload }
      );
      if (resp && resp.ok === false) {
        setSaveError(`Failed to save ${fieldLabel} mapping (${resp.status ?? "error"}).`);
        return;
      }
      setSaveError(null);
      showSavedMessage();
    } catch (err) {
      console.error(`${fieldLabel}: save failed`, err);
      setSaveError(`Failed to save ${fieldLabel} mapping: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // ── delete a single mapping row ───────────────────────────────────────────
  async function deleteRecord(id: number) {
    try {
      await api.messaging.delete(`${apiPrefix}/Mapping/${id}`);
    } catch (err) {
      console.error(`${fieldLabel}: delete failed`, err);
    }
  }

  // ── add a new empty row ───────────────────────────────────────────────────
  const addRow = async () => {
    // A new row starts with a blank field name — if a search filter is active it
    // would never match and the row would look like it silently failed to add.
    if (filterText) onClearFilter?.();
    const newMap: IntegrationFieldMap = {
      id: -1,
      field: { value: "", label: "" },
      create: false,
      usedForSearching: false,
      showWhenSelected: false,
      showInSearch: false,
      defaultValue: "",
      scopeType
    };
    setMappedFields((prev) => [...prev, newMap]);
    // The new row keeps id: -1 until the user's first real edit saves it —
    // `saveRecord`/the server's upsert already treats -1 as "insert" and EF
    // assigns the real identity on that response. A prior version of this
    // function fetched "the last id" from `GET {apiPrefix}/Mapping/` on a
    // timer to guess the new row's id, but that endpoint returns MAX(Id) of
    // EXISTING rows, not a freshly allocated one — it only ever produced a
    // correct value because the old (now-removed) auto-save effect had
    // already POSTed this blank row and inserted it moments earlier. Without
    // that insert, the fetched "last id" belongs to a real, unrelated
    // mapping row; stamping the new row with it made the user's next edit
    // silently overwrite that other row instead of inserting a new one.
  };

  // ── update a single row in state ──────────────────────────────────────────
  const updateMappedField = (index: number, mappedField: IntegrationFieldMap) => {
    const newValue = mappedField.field?.value ?? "";
    const previousValue = mappedFields[index]?.field?.value ?? "";

    // Reject setting a field to a value already used by another row in this
    // same scope — surfaced inline instead of silently accepted or saved.
    // Only runs when the field selection itself actually changed — otherwise
    // an unrelated edit (a checkbox toggle, a default-value keystroke) on a
    // row that already happens to share its field with another row would
    // get rejected too, permanently locking both rows from any further edit.
    if (newValue && newValue !== previousValue) {
      const isDuplicate = mappedFields.some(
        (f, idx) => idx !== index && (f.field?.value ?? "") === newValue
      );
      if (isDuplicate) {
        setFieldErrors((prev) => ({
          ...prev,
          [index]: `"${mappedField.field?.label || newValue}" is already mapped in this scope.`
        }));
        return;
      }
    }

    setFieldErrors((prev) => {
      if (!(index in prev)) return prev;
      const clone = { ...prev };
      delete clone[index];
      return clone;
    });

    // Compute the next array and save it explicitly OUTSIDE the state
    // updater — a setState updater must stay pure (React can invoke it more
    // than once, e.g. under StrictMode), so triggering a network POST from
    // inside one risks a duplicate save.
    const clone = [...mappedFields];
    clone[index] = mappedField;
    setMappedFields(clone);
    saveRecord(clone);
  };

  // ── field-level delete ────────────────────────────────────────────────────
  const handleDeleteFieldClick = (index: number) => {
    const fieldName = mappedFields[index]?.field?.label || "this field";
    setFieldToDelete({ index, fieldName });
    setIsDeleteFieldModalOpen(true);
  };

  const confirmDeleteField = () => {
    if (fieldToDelete === null) return;
    const { index } = fieldToDelete;
    if (mappedFields[index]?.id !== -1) {
      deleteRecord(mappedFields[index].id);
    }
    setMappedFields((prev) => {
      const clone = [...prev];
      clone.splice(index, 1);
      return clone;
    });
    // Row indices shift left by one after the splice above — reindex any
    // pending validation errors so they stay attached to the correct row.
    setFieldErrors((prev) => {
      const clone: Record<number, string> = {};
      Object.entries(prev).forEach(([key, message]) => {
        const i = Number(key);
        if (i === index) return;
        clone[i > index ? i - 1 : i] = message;
      });
      return clone;
    });
    setIsDeleteFieldModalOpen(false);
    setFieldToDelete(null);
  };

  // ── scope-level delete ────────────────────────────────────────────────────
  const handleDeleteRecordClick = (scope: string) => {
    setScopeTypeToDelete(scope);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteRecord = async () => {
    if (!scopeTypeToDelete) return;
    try {
      await api.messaging.delete(
        `${apiPrefix}/Mapping/DeleteRecord/${scopeTypeToDelete}`
      );
      setMappedFields((prev) =>
        prev.filter((f) => f.scopeType !== scopeTypeToDelete)
      );
      // Every row for this scope is gone — any pending per-row validation
      // errors (keyed by index into the old array) no longer refer to
      // anything real and must not re-attach to whatever ends up at that
      // index next.
      setFieldErrors({});
    } catch (err) {
      console.error(`${fieldLabel}: delete record failed`, err);
    }
    setIsDeleteModalOpen(false);
    setScopeTypeToDelete(null);
  };

  // ── render ────────────────────────────────────────────────────────────────
  const chevronClass = `w-4 h-4 transition-transform${expanded ? " rotate-180" : ""}`;

  return (
    <SectionCard
      action={
        <IconButton
          aria-label={expanded ? "Collapse panel" : "Expand panel"}
          onClick={() => setExpanded((s) => !s)}
        >
          <ChevronDown className={chevronClass} />
        </IconButton>
      }
    >
      <div className="mb-4" onClick={(e) => e.stopPropagation()}>
        <CustomSelect
          options={scopeOptionsList}
          value={scopeType}
          width="w-72"
          onChange={setScopeType}
        />
      </div>
      {saveError && (
        <p role="alert" className="mb-4 text-xs text-red-600 border-l-2 border-red-500 pl-3">
          {saveError}
        </p>
      )}
      {expanded && (
        <div>
          <p className="mb-4 text-xs text-zinc-500 leading-relaxed border-l-2 border-zinc-300 pl-3">
            Field mappings tell Velaro how to recognize and display customers from your CRM.
            Mark at least two <strong className="text-zinc-700">Search Key</strong> fields — for example Email and Phone — so contacts who share one value can still be matched reliably during bot conversations and agent searches.
            <strong className="text-zinc-700"> In Results</strong> fields appear when multiple matches are found (both in bot disambiguation and agent search).
            <strong className="text-zinc-700"> Info Panel</strong> fields populate the agent sidebar once a contact is confirmed.
            <strong className="text-zinc-700"> Write Back</strong> fields sync data the bot collects back to your CRM.
          </p>
          <table className="p-4 w-full">
            <thead>
              <tr>
                <th className="pb-4 p-2 pl-4 w-80 text-center text-zinc-700">{fieldLabel}</th>
                <th className="pb-4 p-2 w-80 text-center text-zinc-700">Default value</th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">
                  Write Back
                  <ColumnHelp description="When bot collects this value, write it back to this field in the CRM" />
                </th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">
                  Search Key
                  <ColumnHelp description="Use this field as a key when searching for customer records" />
                </th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">
                  Info Panel
                  <ColumnHelp description="Show this field in the agent info panel when a customer record is matched" />
                </th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">
                  In Results
                  <ColumnHelp description="Show this field in the search results list" />
                </th>
                <th className="pb-4 p-2 w-24 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {mappedFields
                .map((mappedField, i) => ({ mappedField, i }))
                .filter(
                  ({ mappedField }) =>
                    !filterText ||
                    (mappedField.field?.label || "")
                      .toLowerCase()
                      .includes(filterText.toLowerCase())
                )
                .map(({ mappedField, i }) => (
                  <IntegrationMappingRow
                    key={i}
                    mappedField={mappedField}
                    i={i}
                    fieldLabel={fieldLabel}
                    updateMappedField={updateMappedField}
                    isLoading={isLoading}
                    deleteRow={handleDeleteFieldClick}
                    metadata={metadata}
                    errorMessage={fieldErrors[i]}
                  />
                ))}
              {mappedFields.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-xs text-zinc-400 italic">
                    No fields mapped yet. Add a field to enable customer lookup and populate the agent info panel.
                  </td>
                </tr>
              )}
              {mappedFields.length > 0 &&
                filterText &&
                !mappedFields.some((f) =>
                  (f.field?.label || "").toLowerCase().includes(filterText.toLowerCase())
                ) && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-xs text-zinc-400 italic">
                      No fields match "{filterText}".
                    </td>
                  </tr>
                )}
            </tbody>
          </table>

          {scopeType && (
            <div className="flex justify-end pt-4 gap-3">
              <SecondaryButton
                onClick={() => handleDeleteRecordClick(scopeType)}
                disabled={isDeleteDisabled || mappedFields.length === 0}
              >
                Delete record
              </SecondaryButton>
              <PrimaryButton onClick={addRow}>
                Add field
              </PrimaryButton>
            </div>
          )}
        </div>
      )}

      {/* Scope-level delete confirmation */}
      {isDeleteModalOpen && (
        <Modal show={isDeleteModalOpen}>
          <ModalBody>
            <div className="mb-6">
              <div className="text-zinc-800 text-xl font-medium">Delete scope type</div>
            </div>
            <div className="text-center text-zinc-700 text-sm font-normal">
              Are you sure you want to delete all field mappings for{" "}
              <strong>{scopeTypeToDelete}</strong>? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </GhostButton>
              <PrimaryButton onClick={confirmDeleteRecord}>
                Delete
              </PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}

      {/* Field-level delete confirmation */}
      {isDeleteFieldModalOpen && (
        <Modal show={isDeleteFieldModalOpen}>
          <ModalBody>
            <div className="mb-6">
              <div className="text-zinc-800 text-xl font-medium">Delete field mapping</div>
            </div>
            <div className="text-center text-zinc-700 text-sm font-normal">
              Are you sure you want to delete the field mapping for{" "}
              <strong>{fieldToDelete?.fieldName}</strong>? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteFieldModalOpen(false)}>
                Cancel
              </GhostButton>
              <PrimaryButton onClick={confirmDeleteField}>
                Delete
              </PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}
    </SectionCard>
  );
}
